import type {LinkProps} from '@jetbrains/ring-ui/components/link/link'
import Link from '@jetbrains/ring-ui/components/link/link'
import {Directions} from '@jetbrains/ring-ui/components/popup/popup.consts'
import classNames from 'classnames'
import type {ReactNode} from 'react'
import * as React from 'react'
import {useId} from 'react'

import RouterLink from '../../../../../containers/EntityLink'
import {useIsSakuraSelected} from '../../../../../hooks/useIsSakuraSelected'
import useMyId from '../../../../../hooks/useMyId'
import SvgIcon from '../../../../../library/components/SvgIcon/SvgIcon'
import {getIsSakuraUI} from '../../../../../selectors'
import {GUEST_ID} from '../../../../../types'
import {truthy} from '../../../../../utils/guards'
import DarkTooltip from '../../../../common/DarkTooltip/DarkTooltip'
import type {LinkType} from '../../Header.consts'

import styles from './LinkHost.css'

import {useAppSelector} from 'src/hooks/react-redux'

type OwnProps = {
  to?: LinkType
  children?: LinkProps['children']
  className?: string
  innerClassName?: string
  title?: ReactNode
  active?: boolean
  onClick?: LinkProps['onClick']
  icon?: string
  postIcon?: ReactNode
  counter?: number
  counterClassName?: string
  counterAttributes?: Record<string, string>
}
const temporaryClassicUISessionKey = 'isTemporaryClassicUISession'

function LinkHost({
  to,
  children,
  icon,
  className,
  title,
  postIcon,
  counter,
  counterClassName,
  counterAttributes,
  ...props
}: OwnProps) {
  const userId = useMyId()
  const isSakuraSelected = useIsSakuraSelected()
  const isSakuraUI = useAppSelector(getIsSakuraUI)
  const isGuest = userId === GUEST_ID
  const link = React.useMemo(() => {
    if (to == null) {
      return null
    }
    if (to.sakura == null) {
      return {
        to: to.classic,
        sakura: false,
      }
    }

    const isTemporaryClassicUISession =
      window.sessionStorage.getItem(temporaryClassicUISessionKey) === 'true'

    if (isGuest) {
      if (isTemporaryClassicUISession) {
        return {
          to: to.classic,
          sakura: false,
        }
      }

      if (isSakuraUI) {
        return {
          to: to.sakura,
          sakura: true,
        }
      }
    }

    return {
      to: isSakuraSelected && !isTemporaryClassicUISession ? to.sakura : to.classic,
      sakura: isSakuraSelected,
    }
  }, [isSakuraUI, isGuest, isSakuraSelected, to])
  const id = useId()

  if (link != null && link.to == null) {
    return null
  }

  const formatter = new Intl.NumberFormat('en', {notation: 'compact'})
  const content = (
    <>
      {icon && (
        <div className={styles.iconWrapper}>
          <SvgIcon className={styles.icon} icon={icon} />
          {truthy(counter) && (
            <span {...counterAttributes} className={classNames(styles.badge, counterClassName)}>
              {formatter.format(counter).toLowerCase()}
            </span>
          )}
          {postIcon}
        </div>
      )}
      <span>{children}</span>
    </>
  )

  const classes = classNames(styles.link, className, {
    [styles.active]: props.active,
    [styles.withCaption]: children,
  })
  return (
    <DarkTooltip
      id={id}
      delay={100}
      title={title}
      popupProps={{
        directions: [Directions.RIGHT_BOTTOM],
        /* eslint-disable @typescript-eslint/no-magic-numbers */
        top: children ? 8 : 4,
        offset: children ? 8 : 20,
        /* eslint-enable */
      }}
    >
      {isSakuraUI && link?.sakura && link?.to ? (
        <RouterLink
          aria-labelledby={id}
          data-selected={props.active}
          to={link?.to}
          {...props}
          passQueryParameters={false}
          className={classes}
        >
          {content}
        </RouterLink>
      ) : (
        <Link
          aria-labelledby={id}
          data-selected={props.active}
          href={link?.to ?? undefined}
          {...props}
          className={classes}
        >
          {content}
        </Link>
      )}
    </DarkTooltip>
  )
}

export default React.memo(LinkHost)
